import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AgGridReact } from '@ag-grid-community/react';
import { ApLoaderDefault, ApLoaderText } from '@alixpartners/ui-components';
import { getDateFormat } from 'CategoryManagement/utils';
import { getDeepDiveInfo } from 'utils/apiRequests';
import { isUndefined } from 'lodash';
import { formatNumber, formatSpendWithDecimalAndSymbol } from 'utils/number';
import { TableWrapper } from './DeepDiveTable.styles';

const DeepDiveTable = ({
	modelId,
	filterData,
	skuDetails,
	currencySymbol,
	hideDecimals,
}) => {
	const { sku } = skuDetails;
	const [isLoading, setIsLoading] = useState(true);
	const [data, setData] = useState([]);
	const gridRef = useRef();
	const gridStyle = useMemo(() => ({ height: '95%' }), []);
	const defaultColDef = useMemo(() => {
		return {
			flex: 1,
			minWidth: 100,
		};
	}, []);
	const columnDefs = [
		{ headerName: 'SKU', field: 'sku' },
		{ headerName: 'Supplier', field: 'supplier' },
		{
			headerName: 'Purchase Amount',
			field: 'totalSpend',
			valueFormatter: params => {
				if (isUndefined(params.value)) {
					return '';
				} else {
					return formatSpendWithDecimalAndSymbol(
						params.value,
						currencySymbol,
						hideDecimals,
						1
					).toUpperCase();
				}
			},
		},
		{
			headerName: 'Last Price Paid (LPP)',
			field: 'lpp',
			valueFormatter: params => {
				if (isUndefined(params.value)) {
					return '';
				} else {
					return formatSpendWithDecimalAndSymbol(
						params.value,
						currencySymbol,
						hideDecimals,
						1
					).toUpperCase();
				}
			},
		},
		{
			headerName: 'PO Wt.Avg Unit Price',
			field: 'avgUnitPrice',
			valueFormatter: params => {
				if (isUndefined(params.value)) {
					return '';
				} else {
					return formatSpendWithDecimalAndSymbol(
						params.value,
						currencySymbol,
						hideDecimals,
						1
					).toUpperCase();
				}
			},
		},
		{
			headerName: 'Last Purchase Date',
			field: 'lastPurchaseDate',
			valueFormatter: params => {
				if (isUndefined(params.value)) {
					return '';
				} else {
					return params.value.split('T')[0];
				}
			},
		},
		{
			headerName: 'Min Unit Price',
			field: 'minUnitPrice',
			valueFormatter: params => {
				if (isUndefined(params.value)) {
					return '';
				} else {
					return formatSpendWithDecimalAndSymbol(
						params.value,
						currencySymbol,
						hideDecimals,
						1
					).toUpperCase();
				}
			},
		},
		{
			headerName: 'Max Unit Price',
			field: 'maxUnitPrice',
			valueFormatter: params => {
				if (isUndefined(params.value)) {
					return '';
				} else {
					return formatSpendWithDecimalAndSymbol(
						params.value,
						currencySymbol,
						hideDecimals,
						1
					).toUpperCase();
				}
			},
		},
		{
			headerName: 'Total Volume',
			field: 'totalVolume',
			valueFormatter: params => {
				if (isUndefined(params.value)) {
					return '';
				} else {
					return formatNumber(params.value);
				}
			},
		},
		{
			headerName: 'Variance LPP vs Avg Unit price',
			field: 'variance',
			valueFormatter: params => {
				if (isUndefined(params.value)) {
					return '';
				} else {
					return params.value.toFixed(2) + '%';
				}
			},
		},
	];

	useEffect(() => {
		if (sku !== '') {
			const { dateFrom, dateTo } = getDateFormat(
				filterData.dateRange.stateRange
			);
			const payload = {
				filtersDto: {
					sku: [sku],
					category1: filterData.category_1.selectedOption,
					category2: filterData.category_2.selectedOption,
					category3: filterData.category_3.selectedOption,
					commodityCode: filterData.commodity_Code.selectedOption,
					commodityFamily: filterData.commodity_Family.selectedOption,
					originCountry: filterData.origin_Country.selectedOption,
				},
				modelId: modelId,
				startDate: dateFrom,
				endDate: dateTo,
			};
			getDeepDiveInfo(payload).then(res => {
				if (res.succeeded) {
					setData(res.data);
					setIsLoading(false);
				}
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			{isLoading ? (
				<ApLoaderDefault loaderText={() => <ApLoaderText />} />
			) : (
				<TableWrapper>
					<div style={gridStyle} className="ag-theme-balham">
						<AgGridReact
							ref={gridRef}
							rowData={data}
							columnDefs={columnDefs}
							defaultColDef={defaultColDef}
							pagination={true}
							paginationPageSize={20}
						/>
					</div>
				</TableWrapper>
			)}
		</>
	);
};

export default DeepDiveTable;
